import { Col, Form, FormItemProps, Input, Row, Switch } from 'antd'
import { FormInstance } from 'antd/lib'

import { ClassifierCategory, ClassifierType } from 'src/api/requests'
import { IDNP_REGEX, PHONE_REGEX, requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  CountrySelect,
  CUATMTreeSelect,
  InstitutionsSelect
} from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

const { Item } = Form

type Props = {
  form: FormInstance
}

export const ProfileDetailsSection = ({ form }: Props) => {
  const { t } = useLocales()

  const faxRules: FormItemProps['rules'] = [
    {
      pattern: PHONE_REGEX,
      message: t('organizational_structure.phone_error')
    }
  ]

  const institutionType = useClassifierId(
    ClassifierType.INSTITUTION_TYPE,
    ClassifierCategory.INSTITUTION
  )

  const status = Form.useWatch('status', form)
  const isWithoutCode = Form.useWatch('isWithoutCode', form)

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name="idno"
          label={t('organizational_structure.idno')}
          rules={requiredRules?.concat({
            pattern: IDNP_REGEX,
            message: t('organizational_structure.idno_error')
          })}
        >
          <Input
            disabled={isWithoutCode}
            placeholder={t('organizational_structure.idno')}
          />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name="name"
          label={t('organizational_structure.name')}
          rules={requiredRules}
        >
          <Input placeholder={t('organizational_structure.name')} />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name="fiscalCode"
          label={t('other_institutions.fiscal_code')}
          rules={requiredRules}
        >
          <Input placeholder={t('other_institutions.fiscal_code')} />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name="institutionCode"
          label={t('organizational_structure.institution_code')}
          rules={requiredRules}
        >
          <Input
            placeholder={t('organizational_structure.institution_code')}
            maxLength={4}
          />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name="institutionTypeId"
          label={t('organizational_structure.institution_type')}
          rules={requiredRules}
        >
          <ClassifierInfiniteSelect
            id={institutionType}
            placeholder={t('organizational_structure.institution_type')}
          />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name={['contactInfo', 'phone']}
          label={t('organizational_structure.phone')}
          rules={requiredRules?.concat({
            pattern: PHONE_REGEX,
            message: t('organizational_structure.phone_error')
          })}
        >
          <Input placeholder={t('organizational_structure.phone')} />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name={['contactInfo', 'fax']}
          label={t('organizational_structure.fax')}
          rules={faxRules}
        >
          <Input placeholder={t('organizational_structure.fax')} />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name={['contactInfo', 'email']}
          label={t('organizational_structure.email')}
          rules={requiredRules?.concat({ type: 'email' })}
        >
          <Input placeholder={t('organizational_structure.email')} />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name={['address', 'country']}
          label={t('other_institutions.country')}
        >
          <CountrySelect
            defaultValue="md"
            disabled
            placeholder={t('common.country')}
          />
        </Item>
      </Col>

      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name={['address', 'cuatmLocalityId']}
          label={t('organizational_structure.city')}
          rules={requiredRules}
        >
          <CUATMTreeSelect placeholder={t('organizational_structure.city')} />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name={['address', 'street']}
          label={t('organizational_structure.street')}
          rules={requiredRules}
        >
          <Input placeholder={t('organizational_structure.street')} />
        </Item>
      </Col>
      <Col xs={24} md={12} lg={8} xl={6}>
        <Item
          name={['address', 'unitNumber']}
          label={t('other_institutions.number')}
          rules={requiredRules}
        >
          <Input placeholder={t('other_institutions.number')} />
        </Item>
      </Col>

      {isWithoutCode && (
        <Col xs={24} md={12} lg={8} xl={6}>
          <Item
            name="caseDeclarerInstitutionId"
            label={t('organizational_structure.caseDeclarerInstitutionId')}
            rules={requiredRules}
          >
            <InstitutionsSelect popupMatchSelectWidth={false} />
          </Item>
        </Col>
      )}

      <Col span={24}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12} lg={8} xl={6}>
            <Item
              name="status"
              label={`${t('counterparts.status')} (${status ? t('common.active') : t('common.inactive')})`}
              valuePropName="checked"
            >
              <Switch />
            </Item>
          </Col>
          <Col xs={24} md={12} lg={8} xl={6}>
            <Item
              name="declaresCases"
              label={t('organizational_structure.declaresCases')}
              valuePropName="checked"
            >
              <Switch />
            </Item>
          </Col>
          <Col xs={24} md={12} lg={8} xl={6}>
            <Item
              name="tracksCases"
              label={t('organizational_structure.tracksCases')}
              valuePropName="checked"
            >
              <Switch />
            </Item>
          </Col>
          <Col xs={24} md={12} lg={8} xl={6}>
            <Item
              name="isWithoutCode"
              label={t('organizational_structure.isWithoutCode')}
              valuePropName="checked"
            >
              <Switch />
            </Item>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}
